(function () {
  const STORAGE_KEY = 'shopify_recently_viewed_prods';
  const DEFAULT_STORAGE_LIMIT = 20;
  const DEFAULT_EXPIRATION_DAYS = 7;
  const DAY_IN_MS = 24 * 60 * 60 * 1000;

  function isDesignMode() {
    return Boolean(window.Shopify && Shopify.designMode);
  }

  function debugLog(message, detail) {
    if (isDesignMode() || window.sessionStorage.getItem('recentlyViewedDebug') === 'true') {
      console.log(`[RecentlyViewed] ${message}`, detail);
    }
  }

  function sanitizeEntry(entry) {
    if (!entry || typeof entry !== 'object') {
      return null;
    }

    const handle = typeof entry.handle === 'string' ? entry.handle.trim() : '';
    const markup = typeof entry.markup === 'string' ? entry.markup.trim() : '';

    if (!handle || !markup) {
      return null;
    }

    const timestampValue = Number(entry.timestamp);
    const timestamp = Number.isFinite(timestampValue) && timestampValue > 0 ? timestampValue : Date.now();

    return { handle, markup, timestamp };
  }

  function parseStoredEntries(rawValue) {
    if (!rawValue) {
      return [];
    }

    try {
      const parsed = JSON.parse(rawValue);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.map(sanitizeEntry).filter(Boolean);
    } catch (error) {
      console.warn('Unable to parse recently viewed entries.', error);
      return [];
    }
  }

  function readStoredEntries() {
    try {
      const rawValue = window.localStorage.getItem(STORAGE_KEY);
      const entries = parseStoredEntries(rawValue);
      debugLog('Read entries from storage.', { count: entries.length });
      return entries;
    } catch (error) {
      console.warn('Unable to read recently viewed entries.', error);
      return [];
    }
  }

  function writeStoredEntries(entries, limit) {
    const seen = new Set();
    const cleaned = [];

    entries.forEach((entry) => {
      const sanitized = sanitizeEntry(entry);
      if (!sanitized) {
        return;
      }

      if (seen.has(sanitized.handle)) {
        return;
      }

      seen.add(sanitized.handle);
      cleaned.push(sanitized);
    });

    const max = Math.max(1, Number(limit) || DEFAULT_STORAGE_LIMIT);
    const trimmed = cleaned.slice(0, max);

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
      debugLog('Persisted entries to storage.', { count: trimmed.length });
    } catch (error) {
      console.warn('Unable to store recently viewed entries.', error);
    }

    return trimmed;
  }

  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.placeholderMarkup = [];
      this.placeholderClasses = [];
      this.entries = [];
    }

    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.wrapper = this.closest('[data-recently-viewed-wrapper]') || this;
      this.list = this.querySelector('[data-recently-viewed-list]');
      this.placeholders = Array.from(this.querySelectorAll('[data-product-placeholder]'));
      this.placeholderMarkup = this.placeholders.map((item) => item.innerHTML);
      this.placeholderClasses = this.placeholders.map((item) => item.getAttribute('class') || '');

      this.limit = Math.max(1, Number(this.dataset.productsToShow) || 4);
      this.storageLimit = Math.max(1, Number(this.dataset.storageLimit) || DEFAULT_STORAGE_LIMIT);
      this.expirationDays = Math.max(1, Number(this.dataset.expirationDays) || DEFAULT_EXPIRATION_DAYS);
      this.currentHandle = (this.dataset.currentProductHandle || '').trim();
      this.sectionId = (this.dataset.sectionId || '').trim();

      debugLog('Element connected.', {
        sectionId: this.sectionId,
        limit: this.limit,
        storageLimit: this.storageLimit,
        expirationDays: this.expirationDays,
        currentHandle: this.currentHandle,
      });

      this.entries = this.getStoredEntries();
      this.storeCurrentProduct();
      this.render();
    }

    get expirationMs() {
      return Math.max(1, this.expirationDays) * DAY_IN_MS;
    }

    getStoredEntries() {
      const entries = readStoredEntries();
      if (!entries.length) {
        return [];
      }

      const now = Date.now();
      const filtered = entries.filter((entry) => {
        if (!entry) {
          return false;
        }
        const age = now - entry.timestamp;
        if (age > this.expirationMs) {
          debugLog('Discarding expired entry.', { handle: entry.handle, age });
          return false;
        }
        return true;
      });

      if (filtered.length !== entries.length) {
        writeStoredEntries(filtered, this.storageLimit);
      }

      return filtered;
    }

    getCurrentProductPayload() {
      const template = this.querySelector('template[data-current-product-card]');
      if (!template) {
        debugLog('No current product template found.');
        return null;
      }

      if (template.dataset.productVisible === 'false') {
        debugLog('Current product is not visible.');
        return null;
      }

      const handle = (template.dataset.productHandle || '').trim();
      const markup = (template.innerHTML || '').trim();

      if (!handle || !markup) {
        debugLog('Current product template missing handle or markup.');
        return null;
      }

      const timestampAttr = Number(template.dataset.generatedAt);
      const timestamp = Number.isFinite(timestampAttr) && timestampAttr > 0 ? timestampAttr : Date.now();

      return { handle, markup, timestamp };
    }

    storeCurrentProduct() {
      const payload = this.getCurrentProductPayload();
      if (!payload) {
        return;
      }

      const existing = Array.isArray(this.entries) ? this.entries : [];
      const withoutCurrent = existing.filter((entry) => entry.handle !== payload.handle);
      withoutCurrent.unshift(payload);

      this.entries = writeStoredEntries(withoutCurrent, this.storageLimit);

      debugLog('Stored current product markup.', {
        current: payload.handle,
        total: this.entries.length,
      });
    }

    render() {
      if (!this.list || !this.placeholders.length) {
        debugLog('No list or placeholders available.');
        return;
      }

      const entries = Array.isArray(this.entries) ? this.entries : [];
      const renderable = entries.filter(
        (entry) => entry.handle && entry.markup && entry.handle !== this.currentHandle,
      );
      const limited = renderable.slice(0, this.limit);

      if (!limited.length) {
        debugLog('No recently viewed products to render.');
        this.resetPlaceholders({ show: isDesignMode() });
        if (isDesignMode()) {
          this.showSection();
        } else {
          this.hideSection();
        }
        return;
      }

      this.placeholders.forEach((item, index) => {
        const entry = limited[index];
        if (entry) {
          item.innerHTML = entry.markup;
          item.setAttribute('data-product-handle', entry.handle);
          item.removeAttribute('hidden');
        } else {
          this.restorePlaceholder(item, index, { show: false });
        }
      });

      debugLog('Rendered stored products.', { count: limited.length });
      this.showSection();
    }

    restorePlaceholder(item, index, { show = false } = {}) {
      item.innerHTML = this.placeholderMarkup[index] || '';
      const originalClass = this.placeholderClasses[index];
      if (originalClass) {
        item.setAttribute('class', originalClass);
      } else {
        item.removeAttribute('class');
      }
      item.removeAttribute('data-product-handle');
      if (show) {
        item.removeAttribute('hidden');
      } else {
        item.setAttribute('hidden', 'hidden');
      }
    }

    resetPlaceholders({ show = false } = {}) {
      this.placeholders.forEach((item, index) => {
        this.restorePlaceholder(item, index, { show });
      });
    }

    showSection() {
      if (this.wrapper) {
        this.wrapper.removeAttribute('hidden');
      }
      if (this.list) {
        this.list.removeAttribute('hidden');
      }
      this.classList.add('recently-viewed--loaded');
      debugLog('Section shown.');
    }

    hideSection() {
      if (this.wrapper && !isDesignMode()) {
        this.wrapper.setAttribute('hidden', 'hidden');
      }
      if (this.list && !isDesignMode()) {
        this.list.setAttribute('hidden', 'hidden');
      }
      this.classList.remove('recently-viewed--loaded');
      debugLog('Section hidden.');
    }
  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }
})();
